import path from 'path';

import { IdReference, OffsetPagedResults } from '~interfaces';
import { SearchPagedParameters } from '~interfaces/requests';
import {
	BaseReferenceResponse,
	IdReferenceResponse,
	SkcOffsetPagedResponse,
	SkcSingleResponse,
} from '~interfaces/responses';
import { TopologyProxiedSkopeiConnectService } from '~services';

import SkcDeviceGroup, { SkcDeviceGroupNew } from '../interfaces/skcDeviceGroup';

/**
 * A service that does calls to the Skopei Connect API through
 * the Topology backend
 */
class SkcDeviceGroupsService extends TopologyProxiedSkopeiConnectService {
	public readonly path = 'device-groups';

	public readonly devicesSubPath = 'devices';

	async getDeviceGroups({
		page = 1,
		pageSize = 10,
		...args
	}: SearchPagedParameters): Promise<OffsetPagedResults<SkcDeviceGroup>> {
		const { data } = await this._client.get<SkcOffsetPagedResponse<DeviceGroupResponse>>(
			this.path,
			{
				params: {
					'page-number': page,
					'page-size': pageSize,
					'organisation-id': args.organisationId,
					'search-query': args.searchQuery || undefined,
				},
			}
		);

		return this.mapOffsetPagedResponse(data, SkcDeviceGroupsService.fromResponse);
	}

	async getDeviceGroupById(id: string): Promise<SkcDeviceGroup> {
		const { data } = await this._client.get<SkcSingleResponse<DeviceGroupResponse>>(
			path.join(this.path, id)
		);

		return SkcDeviceGroupsService.fromResponse(data.data);
	}

	/**
	 * Create the most basic form of a device group. The devices
	 * needs to be added separately
	 * @param data
	 * @returns
	 */
	async createDeviceGroup(data: SkcDeviceGroupNew): Promise<IdReference> {
		const content = SkcDeviceGroupsService.toRequest(data);

		const response = await this._client.post<SkcSingleResponse<IdReferenceResponse>>(
			this.path,
			content,
			{
				params: {
					'organisation-id': data.organisation.id,
				},
			}
		);

		return this.mapIdResponse(response.data);
	}

	/**
	 * Create a completely new device group. So creating the basic device
	 * group and adding the initial devices
	 * @param data
	 * @returns
	 */
	async createCompleteDeviceGroup(data: SkcDeviceGroupNew): Promise<IdReference> {
		const response = await this.createDeviceGroup(data);

		if (data.devices && data.devices.length > 0) {
			await this.updateDevices(response.id, data.devices);
		}

		return response;
	}

	async updateDevices(groupId: string, devices: IdReference[]): Promise<null> {
		const { data } = await this._client.put<null>(
			path.join(this.path, groupId, this.devicesSubPath),
			devices.map((el) => el.id)
		);

		return data;
	}

	/**
	 * Update the name and description of the device group
	 * @param groupId
	 * @param data
	 * @returns
	 */
	async updateDeviceGroup(groupId: string, data: SkcDeviceGroupNew): Promise<null> {
		const content = SkcDeviceGroupsService.toRequest(data);
		await this._client.put(path.join(this.path, groupId), content);

		return null;
	}

	/**
	 * Updating the group details, assigning and deleting devices are all together
	 * three different calls. Combine it here
	 * @param groupId
	 * @param newData
	 * @param oldData
	 */
	async updateCompleteDeviceGroup(groupId: string, data: SkcDeviceGroupNew): Promise<null> {
		await this.updateDeviceGroup(groupId, data);
		await this.updateDevices(groupId, data.devices);

		return null;
	}

	async deleteDeviceGroup(id: string): Promise<null> {
		await this._client.delete(path.join(this.path, id));

		return null;
	}

	static toRequest(data: SkcDeviceGroupNew): DeviceGroupRequest {
		const { label, description } = data;

		return {
			name: label,
			// Prevent empty strings
			description: description || undefined,
		};
	}

	static fromResponse(data: DeviceGroupResponse): SkcDeviceGroup {
		const { description, devices, organisation, ...rest } = data;

		return {
			...SkcDeviceGroupsService.fromBaseReferenceResponse(rest),
			description: description,
			devices: devices.map((el) => ({
				id: el.id.toString(),
				label: el.name,
				hardwareId: el.hardwareId,
				type: el.deviceType,
			})),
			organisation: SkcDeviceGroupsService.fromBaseReferenceResponse(organisation),
		};
	}
}

interface DeviceResponse extends BaseReferenceResponse {
	hardwareId: string;
	deviceType: string;
	skopeiNr: string;
}

interface DeviceGroupRequest {
	name: string;
	description?: string;
}

interface DeviceGroupResponse extends DeviceGroupRequest, IdReferenceResponse {
	devices: DeviceResponse[];
	organisation: BaseReferenceResponse;
}

export default SkcDeviceGroupsService;
